import type { DiferencaComissao, LancamentoComissao, SubPedido, Representada } from '../db/tipos'
import { dataISOLocal, arredondar2 } from '../pedidos/precos'
import { converterNumeroBR } from '../lib/textoBR'

// O mes de uma comissao e o da LIBERACAO -- decisao dela em 2026-07-30. E o dia
// em que ela marcou "Faturado" (ou "Cliente pagou", nas fornecedoras de gatilho
// pagamento_cliente): o momento em que aquele dinheiro entrou na fila de ser
// pago pela fabrica. Nao existe data de nota fiscal no sistema, e ela preferiu
// nao criar mais um campo para preencher.
export function mesDaComissao(lancamento: LancamentoComissao): string | null {
  return lancamento.data_liberacao ? lancamento.data_liberacao.slice(0, 7) : null
}

export function mesAnterior(mes: string): string {
  const [ano, numero] = mes.split('-').map(Number)
  return numero === 1
    ? `${ano - 1}-12`
    : `${ano}-${String(numero - 1).padStart(2, '0')}`
}

export function mesSeguinte(mes: string): string {
  const [ano, numero] = mes.split('-').map(Number)
  return numero === 12
    ? `${ano + 1}-01`
    : `${ano}-${String(numero + 1).padStart(2, '0')}`
}

const NOMES_DOS_MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

export function rotuloMes(mes: string): string {
  const [ano, numero] = mes.split('-').map(Number)
  return `${NOMES_DOS_MESES[numero - 1]}/${ano}`
}

// Atrasada nao e erro: e o pedido que a fabrica ainda nao pagou. Ele continua
// liberado e reaparece em TODO mes seguinte, com o mes de origem na tarja, ate
// ser pago -- decisao dela: "junto, marcado como atrasado".
export function separarPorMes(
  lancamentos: LancamentoComissao[],
  mes: string,
): { doMes: LancamentoComissao[]; atrasadas: LancamentoComissao[] } {
  const doMes: LancamentoComissao[] = []
  const atrasadas: LancamentoComissao[] = []
  for (const lancamento of lancamentos) {
    const dele = mesDaComissao(lancamento)
    if (dele === null) continue
    if (dele === mes) doMes.push(lancamento)
    else if (dele < mes) atrasadas.push(lancamento)
  }
  const porData = (a: LancamentoComissao, b: LancamentoComissao) =>
    (a.data_liberacao ?? '').localeCompare(b.data_liberacao ?? '')
  return { doMes: doMes.sort(porData), atrasadas: atrasadas.sort(porData) }
}

// Abre no mes anterior, que e a rotina dela ("em agosto ele filtra julho"). Mas
// se nao houver nada la nem antes, abre no mes mais recente que tenha algo: sem
// isso, faturar um pedido hoje e entrar na tela mostra um mes vazio enquanto o
// cartao da fornecedora diz que ha dinheiro a receber.
export function mesInicial(lancamentos: LancamentoComissao[], hoje: Date): string {
  const anterior = mesAnterior(dataISOLocal(hoje).slice(0, 7))
  const { doMes, atrasadas } = separarPorMes(lancamentos, anterior)
  if (doMes.length > 0 || atrasadas.length > 0) return anterior

  const meses = lancamentos
    .map(mesDaComissao)
    .filter((mes): mes is string => mes !== null)
    .sort()
  return meses.length > 0 ? meses[meses.length - 1] : anterior
}

// Um numero SO, com sinal, do ponto de vista DELA (spec 2026-07-30 §4.3):
//   > 0  credito: a fabrica ainda deve a ela  -> selo "faltam R$ X -- cobrar"
//   < 0  debito:  ela deve a fabrica          -> selo "A devolver R$ X"
//
// Antes eram dois calculos separados: estornoPendente em lancamentos.ts e um
// faltaReceber solto no JSX da tela. Enquanto receber a mais era acidente raro
// isso passava; com a devolucao do Ciclo 2 virou caso normal, e o selo unico
// mandava cobrar da fabrica um dinheiro que ela e quem devia.
//
// Continua DERIVADO, nao gravado: guardar numa coluna criaria um numero que pode
// divergir do calculo. O que se grava e so o ACERTO (valor_acertado).
//
// Mora AQUI, e nao em lancamentos.ts, porque este modulo e o das contas puras --
// sem banco e sem tela (spec §6). Com ela do outro lado, o import de
// totalDaBaixa por lancamentos.ts fechava um ciclo entre os dois arquivos.
export function saldoPendente(lancamento: LancamentoComissao): number {
  if (lancamento.status !== 'recebida' || lancamento.valor_recebido === null) return 0
  const devido = lancamento.valor_recalculado ?? lancamento.valor_previsto
  // ?? 0: lancamentos que ja estavam no IndexedDB antes da migration 0013 nao
  // tem a coluna -- o default do servidor so chega neles no proximo pull.
  const acertado = lancamento.valor_acertado ?? 0
  return arredondar2(devido - lancamento.valor_recebido - acertado)
}

// Spec 2026-10-05: saldo em aberto da diferenca do mes (mesmo sinal do
// saldoPendente). Mora aqui, e nao em diferencas.ts, para este modulo continuar
// puro (diferencas.ts grava no banco) -- la ele e reexportado.
export function saldoDaDiferenca(d: DiferencaComissao): number {
  if (d.apagado_em !== null) return 0
  return arredondar2(d.valor - (d.valor_acertado ?? 0))
}

// A soma das pendencias de uma fornecedora, com sinal. Mora aqui porque os DOIS
// lados precisam do mesmo numero: o rodape da tela, que promete um total, e
// darBaixaEmLote, que decide o ajuste gravado. Escrita duas vezes, uma podia
// divergir da outra -- e a tela diria um valor enquanto o banco gravava outro.
export function saldoDaFornecedora(pendentes: LancamentoComissao[]): number {
  return arredondar2(pendentes.reduce((soma, pendente) => soma + saldoPendente(pendente), 0))
}

// Os totais que o topo de cada aba mostra. Pedido dela no smoke: "precisa ter um
// total na parte de cima da tela em todas as abas, isso ajuda a conferencia
// mensal".
//
// O do topo e a soma GERAL, independente da marcacao -- palavras dela. Quem
// responde pela selecao e o rodape (totalDaBaixa): sao perguntas diferentes, e a
// diferenca entre os dois numeros e exatamente o que ela desmarcou ou corrigiu.
export function somaDevida(lancamentos: LancamentoComissao[]): number {
  return arredondar2(
    lancamentos.reduce(
      (soma, lancamento) => soma + (lancamento.valor_recalculado ?? lancamento.valor_previsto),
      0,
    ),
  )
}

// "A funcao do total na aba recebidos e a conferencia do que ele recebeu nas
// empresas" -- ela, 2026-08-01. E o valor GRAVADO, nao o devido.
export function somaRecebida(lancamentos: LancamentoComissao[]): number {
  return arredondar2(
    lancamentos.reduce((soma, lancamento) => soma + (lancamento.valor_recebido ?? 0), 0),
  )
}

// Dois numeros, NUNCA um liquido. O liquido sozinho engana: +100 com -100 daria
// "R$ 0,00" com duas dividas abertas logo abaixo, na mesma tela. E o mesmo furo
// que a Task 8 fechou no cartao, por outra porta.
//
// Amarelo "A devolver" e ela quem deve a fabrica; vermelho "A cobrar" e a
// fabrica que deve a ela. Inverter isso vira divida em dobro, e o dinheiro e
// real -- por isso os dois lados nunca se encontram numa soma.
export function totaisDePendencia(
  lancamentos: LancamentoComissao[],
): { aCobrar: number; aDevolver: number } {
  let aCobrar = 0
  let aDevolver = 0
  for (const lancamento of lancamentos) {
    const saldo = saldoPendente(lancamento)
    if (saldo > 0) aCobrar += saldo
    else if (saldo < 0) aDevolver += -saldo
  }
  return { aCobrar: arredondar2(aCobrar), aDevolver: arredondar2(aDevolver) }
}

// O valor que ela conferiu numa linha: por padrao o devido, e o que ela digitou
// quando a fabrica pagou outro valor. E daqui que sai o valor_recebido gravado,
// entao texto invalido NAO pode passar adiante -- 'abc' viraria NaN e
// contaminaria o total do lote e o saldo depois dele.
//
// Negativo tambem cai fora: quem cria divida e a devolucao, nao a baixa. Para
// dizer "esta nao veio", ela DESMARCA a linha; zero, por outro lado, e legitimo.
export function valorConferido(devido: number, digitado: string | undefined): number {
  if (digitado === undefined) return devido
  const convertido = converterNumeroBR(digitado)
  return convertido === null || convertido < 0 ? devido : convertido
}

// O que o campo de valor aceita RECEBER. Pedido dela no smoke: "ou proibir
// digitar letras nesse campo, acho que assim acaba o problema" -- o "O" de
// "4O,00" nao deve nem entrar.
//
// Digitos e pontos livres porque o milhar "1.234,56" e o formato dela; no
// maximo UMA virgula, e depois dela so digito. O vazio passa: ela precisa poder
// apagar para redigitar.
//
// ⚠ Isto e conveniencia de tela, NAO defesa do dinheiro, e nao acaba com o
// problema: "1.2.3", ".", "," e o vazio passam por aqui e converterNumeroBR
// devolve null nos quatro -- medido. Nao da para distinguir "1.234" de "1.2.3"
// enquanto ela digita. Quem impede um texto de virar NaN em cima do
// valor_recebido gravado continua sendo valorConferido, logo acima, e o aviso
// da tela continua sendo necessario.
export function aceitaDigitacao(texto: string): boolean {
  return /^[\d.]*(,\d*)?$/.test(texto)
}

export interface CartaoFornecedora {
  representadaId: string
  nome: string
  // O que a tela de dentro vai LISTAR em "A receber" neste mes: as do mes MAIS
  // as atrasadas. Nao e "so o mes" -- ver entraNoMes.
  aReceber: number
  quantidade: number
  // Todas as liberadas, de qualquer mes. Vira a dica cinza do cartao, e e ela
  // quem decide se o cartao EXISTE: filtrar a existencia pelo mes faria sumir a
  // fornecedora que so tem dinheiro em outro mes.
  aReceberTotal: number
  previstas: number
  saldo: number
  // Quantas linhas estao em aberto, nao o liquido delas. O saldo sozinho nao
  // basta: credito e debito que se anulam dao saldo 0, e sem esta contagem a
  // fornecedora inteira sumiria da lista -- ver o filtro no fim da funcao.
  pendencias: number
  // Spec 2026-10-04: o debito/credito que sobra passa para os meses seguintes
  // com a referencia do mes em que nasceu. E o mes de comissao MAIS ANTIGO entre
  // as linhas pendentes; null quando nao ha pendencia.
  refPendencia: string | null
}

// O primeiro numero que ela ve passa a ser O QUE A FABRICA DEVE, nao uma lista
// por pedido e tabela: "fica dificil eu saber qual eu recebi" (2026-07-30). A
// fabrica paga um valor so, no mes seguinte -- nao ha como bater um sub-pedido
// contra um deposito.
//
// A MESMA regra que separarPorMes usa para montar a aba "A receber": o mes
// escolhido, mais tudo que ficou para tras e ainda nao foi pago. Os dois lados
// PRECISAM concordar -- se o cartao somasse so as do mes, uma atrasada de R$ 35
// faria ele dizer 400 e a tela de dentro 435.
function entraNoMes(lancamento: LancamentoComissao, mes: string): boolean {
  const dele = mesDaComissao(lancamento)
  return dele !== null && dele <= mes
}

// O cartao agora e de um MES, porque ela confere mes a mes contra o extrato da
// fabrica: antes ele somava tudo e a tela de dentro abria num mes so, e nada
// explicava a diferenca (medido no smoke: R$ 1.020 no cartao, R$ 400 na tela).
// O retrato inteiro nao se perdeu -- virou aReceberTotal.
export function cartoesPorFornecedora(
  lancamentos: LancamentoComissao[],
  subPedidos: SubPedido[],
  representadas: Representada[],
  mes: string,
  // Spec 2026-10-05: a diferenca do mes entra no saldo, nas pendencias e no ref.
  diferencas: DiferencaComissao[] = [],
): CartaoFornecedora[] {
  const representadaDoSub = new Map(subPedidos.map((s) => [s.id, s.representada_id]))
  const porId = new Map<string, CartaoFornecedora>()

  for (const lancamento of lancamentos) {
    const representadaId = representadaDoSub.get(lancamento.sub_pedido_id)
    if (representadaId === undefined) continue // orfao: nao derruba a tela
    const representada = representadas.find((r) => r.id === representadaId)
    if (!representada) continue

    const cartao = porId.get(representadaId) ?? {
      representadaId, nome: representada.nome,
      aReceber: 0, quantidade: 0, aReceberTotal: 0, previstas: 0, saldo: 0, pendencias: 0,
      refPendencia: null,
    }
    if (lancamento.status === 'liberada') {
      const valor = lancamento.valor_recalculado ?? lancamento.valor_previsto
      cartao.aReceberTotal = arredondar2(cartao.aReceberTotal + valor)
      if (entraNoMes(lancamento, mes)) {
        cartao.aReceber = arredondar2(cartao.aReceber + valor)
        cartao.quantidade += 1
      }
    } else if (lancamento.status === 'prevista') {
      cartao.previstas = arredondar2(
        cartao.previstas + (lancamento.valor_recalculado ?? lancamento.valor_previsto),
      )
    } else if (lancamento.status === 'recebida') {
      // Exaustivo de proposito, e nao um `else`: um quarto status no union
      // cairia aqui e seria contado como pendencia no cartao, enquanto a tela de
      // dentro (que filtra por `status === 'recebida'`) nao o mostraria. Os dois
      // niveis passariam a discordar -- justo o que este ciclo acabou de alinhar.
      const pendente = saldoPendente(lancamento)
      cartao.saldo = arredondar2(cartao.saldo + pendente)
      if (pendente !== 0) {
        cartao.pendencias += 1
        const mesDela = mesDaComissao(lancamento)
        if (mesDela !== null && (cartao.refPendencia === null || mesDela < cartao.refPendencia)) {
          cartao.refPendencia = mesDela
        }
      }
    }
    porId.set(representadaId, cartao)
  }

  for (const d of diferencas) {
    const saldoDela = saldoDaDiferenca(d)
    if (saldoDela === 0) continue
    const representada = representadas.find((r) => r.id === d.representada_id)
    if (!representada) continue
    const cartao = porId.get(d.representada_id) ?? {
      representadaId: d.representada_id, nome: representada.nome,
      aReceber: 0, quantidade: 0, aReceberTotal: 0, previstas: 0, saldo: 0, pendencias: 0,
      refPendencia: null,
    }
    cartao.saldo = arredondar2(cartao.saldo + saldoDela)
    cartao.pendencias += 1
    if (cartao.refPendencia === null || d.mes < cartao.refPendencia) cartao.refPendencia = d.mes
    porId.set(d.representada_id, cartao)
  }

  // "Fornecedora com tudo zerado nao vira cartao" (decisao dela) -- mas uma
  // pendencia em aberto NAO e zerado, mesmo quando o liquido da zero. Sem o
  // `pendencias > 0` aqui, um credito de +100 com um debito de -100 fazia a
  // fornecedora desaparecer, e com ela o unico caminho ate o botao "Pago", que
  // e a unica saida desse par (a baixa em lote nao o alcanca: ajuste = 0).
  //
  // ⚠ E o TOTAL que decide, nao o mes: com o filtro de mes olhando aqui, uma
  // fornecedora que so tem dinheiro em agosto sumiria da lista em julho, e com
  // ela o unico caminho ate a aba Debitos/Creditos.
  return [...porId.values()]
    .filter(
      (c) => c.aReceberTotal !== 0 || c.previstas !== 0 || c.saldo !== 0 || c.pendencias > 0,
    )
    // Maior do MES em cima, porque a conferencia e do mes. Quem esta zerado no
    // mes desempata pelo total, e so entao pelo nome -- sem isso todas as
    // zeradas ficariam em ordem alfabetica arbitraria.
    .sort(
      (a, b) =>
        b.aReceber - a.aReceber ||
        b.aReceberTotal - a.aReceberTotal ||
        a.nome.localeCompare(b.nome),
    )
}

export interface TotalDaBaixa {
  selecionados: number
  ajuste: number
  total: number
}

// A conta que ela compara com o extrato. Fica fixa no rodape porque ela CONFERE
// aritmetica -- mostrar o resultado enquanto ela decide e o que desfaz
// ambiguidade (licao do Ciclo 2, da caixa de quantidade da devolucao).
//
// O credito soma sem limite; o DEBITO e limitado pelo que esta marcado, senao o
// total ficaria negativo -- a fabrica desconta o que cabe e o resto fica para o
// mes seguinte.
export function totalDaBaixa(valoresSelecionados: number[], saldo: number): TotalDaBaixa {
  const selecionados = arredondar2(valoresSelecionados.reduce((soma, v) => soma + v, 0))
  const ajuste = saldo >= 0 ? saldo : Math.max(saldo, -selecionados)
  return { selecionados, ajuste: arredondar2(ajuste), total: arredondar2(selecionados + ajuste) }
}
